import { components } from "@classdojo/ts-api-types/api";
import combineActionHandlers from "@web-monorepo/infra/combineActionHandlers";
import createAction from "@web-monorepo/infra/createAction";
import createActionHandler from "@web-monorepo/infra/createActionHandler";
import createAsyncActions from "@web-monorepo/infra/createAsyncActions";
import { PodInstallFunction } from "@web-monorepo/shared/podInfra";
import createApiSaga from "src/utils/createApiSaga";

const STATE_KEY = "parentStudents";

type Student = components["schemas"]["RenderedStudentForParent"];

type ParentStudentsState = {
  students: Student[];
  parentId: string | null;
  fetchingStudents?: boolean;
};

const initialState: ParentStudentsState = {
  students: [],
  parentId: null,
};

export type ParentStudentsSlice = {
  [STATE_KEY]: ParentStudentsState;
};

// Actions
const [GET_STUDENTS, GET_STUDENTS_DONE, GET_STUDENTS_FAIL] = createAsyncActions(`${STATE_KEY}/getStudents`);

const SET_PARENT_ID = createAction(`${STATE_KEY}/setParentId`);

// Action Creators
export function getStudents(parentId: string) {
  return {
    type: GET_STUDENTS,
    payload: {
      parentId,
    },
  };
}

export function setParentId(parentId: string | string[]) {
  return {
    type: SET_PARENT_ID,
    payload: {
      parentId,
    },
  };
}

// Selectors
export const selectStudents = (state: ParentStudentsSlice) => {
  return state?.[STATE_KEY]?.students;
};

export const hasStudents = (state: ParentStudentsSlice) => {
  return selectStudents(state).length > 0;
};

export const selectParentId = (state: ParentStudentsSlice) => {
  return state?.[STATE_KEY].parentId;
};

export const hasParentId = (state: ParentStudentsSlice) => {
  return !!selectParentId(state);
};

// Sagas
const getStudentsApiSaga = createApiSaga(
  GET_STUDENTS,
  ({ parentId }: { parentId: string }) => {
    return {
      method: "GET",
      path: `/api/parent/${parentId}/student`,
      query: { groupBy: "student" },
    };
  },
  GET_STUDENTS_DONE,
);

// Reducers
const getStudentsHandler = createActionHandler(GET_STUDENTS, () => {
  return (state: ParentStudentsState): ParentStudentsState => ({
    ...state,
    fetchingStudents: true,
  });
});

const getStudentsFailHandler = createActionHandler(GET_STUDENTS_FAIL, () => {
  return (state: ParentStudentsState): ParentStudentsState => ({
    ...state,
    fetchingStudents: false,
  });
});

const getStudentsDoneHandler = createActionHandler(
  GET_STUDENTS_DONE,
  ({ response }: { response: { _items: Student[] } }) => {
    return (state: ParentStudentsState): ParentStudentsState => ({
      ...state,
      students: response._items,
      fetchingStudents: false,
    });
  },
);

const setParentIdHandler = createActionHandler(SET_PARENT_ID, ({ parentId }: { parentId: string }) => {
  return (state: ParentStudentsState): ParentStudentsState => ({
    ...state,
    parentId,
  });
});

// Final reducer
const finalReducer = combineActionHandlers(initialState, [
  getStudentsHandler,
  getStudentsFailHandler,
  getStudentsDoneHandler,
  setParentIdHandler,
]);

const install: PodInstallFunction = (installReducer, installSaga) => {
  installReducer(STATE_KEY, finalReducer);
  installSaga(getStudentsApiSaga);
};

export default install;
